import React, { useState, createRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import {
  Select as MuiSelect,
  MenuItem,
  FormHelperText,
  FormControl,
  Checkbox,
  ListItemText,
  InputLabel,
  Input
} from '@material-ui/core';

import useStyles from './commonStyles';
import CustomInputLabel from './CustomInputLabel';

const MultiSelect = props => {
  const classes = useStyles();
  const { t } = useTranslation();

  let {
    variant = 'standard',
    customLabel = true,
    search = false,
    label, name, value, onChange, disabled, options, helperText,
    ...rest
  } = props;

  if(!Array.isArray(value)) {
    value = [];
  }

  const [ searchValue, setSearchValue ] = useState('');
  const [ currentOptions, setCurrentOptions ] = useState(options);
  const ref = createRef();

  useEffect(() => {
    setCurrentOptions(options);
  }, [options]);

  const handleSearch = event => {
    const newSearchValue = event.target.value;

    const newOptions = options.filter(option => {
      // Always show selected even if filtering via search
      if(value.includes(option.value)) {
        return true;
      }

      // Show those that match search keywords
      return option.label.toLowerCase().includes(newSearchValue.toLowerCase());
    });

    setSearchValue(newSearchValue);
    setCurrentOptions(newOptions);
  }

  return (
    <FormControl fullWidth variant={variant}>
      {customLabel ? <CustomInputLabel id={`multiselect-${name}`}>{label}</CustomInputLabel> : <InputLabel id={`multiselect-${name}`}>{label}</InputLabel>}
      <MuiSelect
        labelId={`multiselect-${name}`}
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
        fullWidth={true}
        inputProps={{
          classes: {
            icon: classes.selectIcon,
            root: classes.select
          },
        }}
        multiple
        renderValue={(selected) => selected.map(id => options.find(opt => opt.value === id)?.label).join(', ')}
        MenuProps={{
          getContentAnchorEl: () => null,
          keepMounted: true
        }}
        {...rest}
      >
        {search
          ?
          <div className={classes.selectSearch}>
            <Input className={classes.selectSearchInput} fullWidth ref={ref} onChange={handleSearch} value={searchValue} placeholder={t('common:search')} onClick={e => e.stopPropagation()} onKeyDown={e => {e.stopPropagation()}} />
          </div>
          : null
        }
        {currentOptions.map(option =>
          <MenuItem key={`select-${name}-${option.value}`} value={option.value}>
            <Checkbox color="primary" checked={value.includes(option.value)} />
            <ListItemText primary={option.label} />
          </MenuItem>
        )}
      </MuiSelect>
      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
    </FormControl>
  );
}

MultiSelect.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })),
  helperText: PropTypes.string,
  variant: PropTypes.oneOf(['filled', 'outlined', 'standard']),
  customLabel: PropTypes.bool,
  search: PropTypes.bool
};

export default MultiSelect;
